import { ColumnDef } from '@tanstack/react-table';
import { useMemo } from 'react';
import { useAppContext } from 'src/contexts/AppContext';
import { useTagsForEntityType } from 'src/hooks/useTagsForEntityType';
import { useServerUserSetting } from 'src/hooks/useUserSetting';
import { MainRoute } from 'src/navigations/Routes/MainRoute';
import {
  PURCHASE_TABLE_COLUMNS_CONFIG,
  tagColumnDef,
} from 'src/tables/PurchaseTable/configs/PurchaseTableColumnsConfig';
import { getPurchaseTableColumnsConfigV2 } from 'src/tables/PurchaseTable/configs/PurchaseTableColumnsConfigV2';
import { createTagColumn } from 'src/tables/TableV2/factories/tagColumnFactory';
import {
  defaultPurchaseEventEnabledColumnsConfig,
  defaultPurchasesColumnsConfig,
  filterColumnsByFeatures,
  getPurchasesColumnConfigById,
} from 'src/utils/columns/columnUtils';
import { PurchasesTableColumnId } from 'src/utils/columns/purchases/purchasesColumnUtils.types';
import { CustomPurchasesColumn } from 'src/utils/columns/purchases/purchasesCustomColumnUtils.types';
import { SectionType } from 'src/utils/types/sectionType';
import { hasAllPermissions } from 'src/utils/userPermissionUtils';
import { hasFeatures } from 'src/utils/userUtils';
import {
  ActionOutboxEntityType,
  PurchaseOrderTicketGroup,
  PurchaseViewMode,
  UserSetting,
} from 'src/WebApiController';

interface UseGetPurchasesTableColumnsProps {
  groupId: string; // First of month
  viewMode: PurchaseViewMode;
  columnVisibilityOverride?: PurchasesTableColumnId[];
  isSideTable?: boolean;
  showCheckbox?: boolean;
  hideActionOnCheckbox?: boolean;
  useV2?: boolean; // Use V2 column configs
  isSearchV2?: boolean; // Disable sorting for non-sortable columns in Search V2
}

export const useGetPurchasesTableColumns = ({
  viewMode,
  columnVisibilityOverride,
  isSideTable,
  showCheckbox,
  hideActionOnCheckbox = true,
  useV2 = false,
  isSearchV2 = false,
}: UseGetPurchasesTableColumnsProps) => {
  const { appContext, loginContext } = useAppContext();
  const { mainRoute } = useAppContext();

  // Event tile view only available in Purchases portfolio page
  const isEventTileView = useMemo(
    () =>
      viewMode === PurchaseViewMode.EventTileView &&
      mainRoute === MainRoute.Purchases,
    [mainRoute, viewMode]
  );

  const [defaultColumnOrder, columnOrderUserSettingId] = useMemo(() => {
    const defaultColumnOrder = isEventTileView
      ? defaultPurchaseEventEnabledColumnsConfig
      : defaultPurchasesColumnsConfig;
    const columnOrderUserSettingId = isSideTable
      ? UserSetting.PurchaseSideTableColumnOrder
      : isEventTileView
        ? UserSetting.PurchaseEventColumnOrder
        : UserSetting.PurchaseColumnOrder;

    return [defaultColumnOrder, columnOrderUserSettingId];
  }, [isEventTileView, isSideTable]);
  const { value: storedPurchaseColumnOrderSetting = defaultColumnOrder } =
    useServerUserSetting<PurchasesTableColumnId[]>({
      id: columnOrderUserSettingId,
    });

  const [defaultColumnVisible, columnVisibleUserSettingId] = useMemo(() => {
    const defaultColumnOrder = isEventTileView
      ? defaultPurchaseEventEnabledColumnsConfig
      : undefined;
    const columnOrderUserSettingId = isSideTable
      ? UserSetting.PurchaseSideTableColumnsEnabled
      : isEventTileView
        ? UserSetting.PurchaseEventColumnsEnabled
        : UserSetting.PurchaseColumnsEnabled;

    return [defaultColumnOrder, columnOrderUserSettingId];
  }, [isEventTileView, isSideTable]);
  const { value: storedPurchaseColumnsEnabledSetting = defaultColumnVisible } =
    useServerUserSetting<string[]>({
      id: columnVisibleUserSettingId,
    });

  const { tagsMetadata } = useTagsForEntityType(
    ActionOutboxEntityType.Purchase,
    true
  );

  return useMemo(() => {
    const customPurchaseColumnsFiltered: CustomPurchasesColumn[] = [];
    const columnsConfig = useV2
      ? getPurchaseTableColumnsConfigV2(isSearchV2)
      : PURCHASE_TABLE_COLUMNS_CONFIG;

    const purchaseColumnOrder = columnVisibilityOverride
      ? columnVisibilityOverride
      : storedPurchaseColumnOrderSetting.filter(
          // If storedPurchaseColumnsEnabledSetting has never been set, default to true
          (c) => storedPurchaseColumnsEnabledSetting?.includes(c) ?? true
        );

    const columns = new Set(
      columnVisibilityOverride
        ? columnVisibilityOverride
            .map((cid) => columnsConfig.find((c) => c.id === cid)!)
            .map((item) => item.id!)
        : [
            // Add unconfigurable columns first
            ...Object.values(columnsConfig)
              .filter((column) => {
                const columnDef = getPurchasesColumnConfigById(
                  column.id as PurchasesTableColumnId
                ).personalization;
                const hasFeatureForColumn =
                  columnDef.requiredFeatures.length === 0 ||
                  hasFeatures(
                    loginContext?.user,
                    appContext?.features,
                    columnDef.requiredFeatures
                  );
                const hasPermissionForColumn =
                  columnDef.requiredPermissions.length === 0 ||
                  hasAllPermissions(
                    loginContext?.user,
                    columnDef.requiredPermissions
                  );

                const visibleInCurrentViewMode =
                  columnDef.visibleViewModes.length === 0 ||
                  columnDef.visibleViewModes.includes(viewMode);

                const isConfigurable = columnDef.isConfigurable;

                return (
                  !isConfigurable &&
                  hasFeatureForColumn &&
                  hasPermissionForColumn &&
                  visibleInCurrentViewMode
                );
              })
              .map((item) => item.id as PurchasesTableColumnId),
            // Add user defined columns next
            ...filterColumnsByFeatures(
              purchaseColumnOrder,
              SectionType.Purchases,
              customPurchaseColumnsFiltered,
              loginContext?.user,
              appContext?.features,
              tagsMetadata ?? [],
              undefined,
              viewMode
            ),
          ]
    );

    if (!columnVisibilityOverride) {
      if (showCheckbox) {
        if (hideActionOnCheckbox) {
          columns.delete(PurchasesTableColumnId.Actions);
        }
      } else {
        columns.delete(PurchasesTableColumnId.Checkbox);
      }
    }

    return Array.from(columns).reduce<
      ColumnDef<PurchaseOrderTicketGroup | null>[]
    >((acc, columnId) => {
      if (tagsMetadata?.find((t) => t.key === columnId)) {
        acc.push(
          useV2
            ? createTagColumn<PurchaseOrderTicketGroup | null>({
                columnId,
                tagsAccessor: (data) => data?.tags,
                currencyAccessor: (data) => data?.currency,
                sectionType: SectionType.Purchases,
                ...(isSearchV2 && { enableSorting: false }),
              })
            : tagColumnDef(columnId)
        );
      } else {
        const columnConfig = Object.values(columnsConfig).find(
          (column) => column.id === columnId
        );
        if (columnConfig) {
          acc.push(columnConfig);
        }
      }
      return acc;
    }, []);
  }, [
    appContext?.features,
    columnVisibilityOverride,
    hideActionOnCheckbox,
    isSearchV2,
    loginContext?.user,
    showCheckbox,
    storedPurchaseColumnOrderSetting,
    storedPurchaseColumnsEnabledSetting,
    tagsMetadata,
    viewMode,
    useV2,
  ]);
};

export const usePurchasesTableColumnIdOrder = ({
  viewMode,
  columnVisibilityOverride,
  isSideTable,
}: {
  viewMode: PurchaseViewMode;
  columnVisibilityOverride?: PurchasesTableColumnId[];
  isSideTable?: boolean;
}) => {
  const {
    value: storedPurchaseColumnOrderSetting = viewMode ===
    PurchaseViewMode.EventTileView
      ? defaultPurchaseEventEnabledColumnsConfig
      : defaultPurchasesColumnsConfig,
  } = useServerUserSetting<PurchasesTableColumnId[]>({
    id: isSideTable
      ? UserSetting.PurchaseSideTableColumnOrder
      : viewMode === PurchaseViewMode.EventTileView
        ? UserSetting.PurchaseEventColumnOrder
        : UserSetting.PurchaseColumnOrder,
  });
  const {
    value: storedPurchaseColumnsEnabledSetting = viewMode ===
    PurchaseViewMode.EventTileView
      ? defaultPurchaseEventEnabledColumnsConfig
      : undefined,
  } = useServerUserSetting<string[]>({
    id: isSideTable
      ? UserSetting.PurchaseSideTableColumnsEnabled
      : viewMode === PurchaseViewMode.EventTileView
        ? UserSetting.PurchaseEventColumnsEnabled
        : UserSetting.PurchaseColumnsEnabled,
  });

  const purchaseColumnIdOrder = useMemo(
    () =>
      columnVisibilityOverride
        ? columnVisibilityOverride
        : storedPurchaseColumnOrderSetting.filter(
            // If storedPurchaseColumnsEnabledSetting has never been set, default to true
            (c) => storedPurchaseColumnsEnabledSetting?.includes(c) ?? true
          ),
    [
      columnVisibilityOverride,
      storedPurchaseColumnOrderSetting,
      storedPurchaseColumnsEnabledSetting,
    ]
  );

  return { purchaseColumnIdOrder };
};
